import { formatRegionalError, RegionalApiError } from '@/apiClients/regionalApi';
import { clearRegionalAuthError, useRegionalAuthErrorStore } from '@/apiClients/regionalAuthErrors';
import { Capabilities } from '@/auth-capabilities';
import { Alert, Button } from '@/components/ui';
import { ApiRegion } from '@/config';
import { useAuth } from '@/context/appContext';
import { useRegionalApis } from '@/context/RegionScope';

/**
 * Inline US auth failure. It never signs the Canada session out. Pages that mix regions
 * pass `region` to render it once above the list, outside any row scope.
 */
export const RegionalAuthErrorBanner = (props: { region?: ApiRegion } = {}) => {
  const scoped = useRegionalApis();
  const region = props.region ?? scoped.region;
  const { hasAuth } = useAuth();
  const error = useRegionalAuthErrorStore(state => state.errors[region]);
  if (region === ApiRegion.CA || !error) {
    return null;
  }
  // A mint failure means Canada rejected the Canada token: saying "Canada is still signed in" would be wrong.
  const isMintFailure = error instanceof RegionalApiError && error.mintFailure;
  return (
    <Alert tone="critical" role="alert" className="mb-2 flex items-start justify-between gap-3">
      <span>
        {hasAuth(Capabilities.commonDataReviewerAdmin) ? (
          <>
            {formatRegionalError(error)}
            {!isMintFailure && ' Your Canada session is still signed in.'}
          </>
        ) : isMintFailure ? (
          'Your session has expired. Sign in again.'
        ) : (
          'Unable to connect to the review service. Please try again.'
        )}
      </span>
      <Button
        variant="ghost"
        size="small"
        className="text-critical hover:bg-critical/10 hover:text-critical"
        onClick={() => clearRegionalAuthError(region)}
      >
        Dismiss
      </Button>
    </Alert>
  );
};
